import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { parseISO, toISO } from "@/lib/format";

const isoToDmy = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const dmyToIso = (t) => {
  const m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return d.getDate() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 ? toISO(d) : null;
};
const mask = (d) => d.slice(0, 2) + (d.length > 2 ? `/${d.slice(2, 4)}` : "") + (d.length > 4 ? `/${d.slice(4, 8)}` : "");
const caretFor = (n) => n + (n > 2 ? 1 : 0) + (n > 4 ? 1 : 0);

// Re-format typed text while keeping the caret where the user is editing.
const edit = (raw, caret, prevText, forward) => {
  let digits = raw.replace(/\D/g, "");
  let before = raw.slice(0, caret).replace(/\D/g, "").length;
  const prevDigits = prevText.replace(/\D/g, "");
  if (raw.length < prevText.length && digits === prevDigits) {
    if (forward) digits = digits.slice(0, before) + digits.slice(before + 1);
    else if (before > 0) {
      digits = digits.slice(0, before - 1) + digits.slice(before);
      before -= 1;
    }
  }
  if (digits.length > 8) digits = digits.slice(0, before) + digits.slice(before + digits.length - 8);
  return { text: mask(digits.slice(0, 8)), caret: caretFor(Math.min(before, 8)) };
};

export const DateInput = ({ value, onChange, testid, min, required, className = "" }) => {
  const [text, setText] = useState(isoToDmy(value));
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const caretRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => { setText(isoToDmy(value)); }, [value]);
  useLayoutEffect(() => {
    if (caretRef.current === null || document.activeElement !== inputRef.current) return;
    inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
    caretRef.current = null;
  }, [text]);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    panelRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const type = (e) => {
    const forward = e.nativeEvent?.inputType === "deleteContentForward";
    const r = edit(e.target.value, e.target.selectionStart ?? e.target.value.length, text, forward);
    caretRef.current = r.caret;
    setText(r.text);
    if (!r.text && !required) onChange("");
    const iso = dmyToIso(r.text);
    if (iso && (!min || iso >= min)) onChange(iso);
  };
  const blur = () => {
    const iso = dmyToIso(text);
    if (!text ? required : !iso || (min && iso < min)) setText(isoToDmy(value));
  };
  const pick = (d) => {
    if (d) onChange(toISO(d));
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <Input ref={inputRef} value={text} onChange={type} onBlur={blur} placeholder="dd/mm/yyyy" inputMode="numeric" required={required}
        className="h-12 pr-12 tabular-nums" data-testid={testid} />
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Open calendar" aria-expanded={open} data-testid={`${testid}-calendar-btn`}
        className="absolute right-1 top-1.5 w-10 h-9 rounded-lg grid place-items-center text-slate-500 hover:bg-sand [touch-action:manipulation]">
        <CalendarDays className="w-5 h-5" />
      </button>
      {open && (
        <div ref={panelRef} className="absolute right-0 top-full mt-1 z-[60] bg-white border border-border rounded-xl shadow-lg" data-testid={`${testid}-calendar`}>
          <Calendar mode="single" selected={value ? parseISO(value) : undefined} defaultMonth={value ? parseISO(value) : undefined}
            disabled={min ? { before: parseISO(min) } : undefined} weekStartsOn={1} onSelect={pick} />
        </div>
      )}
    </div>
  );
};
